import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { ThemeSync } from "@/components/theme-sync";
import { AuthProvider } from "@/hooks/use-auth";
import { I18nProvider } from "@/i18n";
import { queryClient } from "@/lib/query";
import { router } from "@/router";
import { applyAppearancePreferences } from "@/lib/appearance-preferences";

// A view transition that is cut short rejects its promise with an error
// nobody awaits: an AbortError when another navigation wins (two quick
// clicks), an InvalidStateError when the tab is hidden mid-way ("Document
// hidden"). Either way the page simply changes without the animation, which
// is right, so it is not reported as an uncaught error. Anything else still is.
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason as { name?: string; message?: string } | undefined;
  if (
    (reason?.name === "AbortError" || reason?.name === "InvalidStateError") &&
    /transition/i.test(reason.message ?? "")
  ) {
    event.preventDefault();
  }
});
// The fonts, served from our own origin with the rest of the build. Each
// family is split by script (unicode-range), so a page downloads only the
// Cyrillic and Latin files it actually draws.
import "@fontsource-variable/onest";
import "@fontsource-variable/unbounded";
import "@fontsource/russo-one/cyrillic-400.css";
import "@fontsource/russo-one/latin-400.css";
import "@/index.css";
import { FEATURES } from "@/lib/features";

// A deploy replaces every built asset with a freshly-hashed filename — a tab
// left open across that moment still has the *old* index.html in memory, so
// its next route-level lazy import points at a chunk that no longer exists
// on the server. Vite recognizes exactly this and fires `vite:preloadError`
// instead of letting it surface as a bare fetch failure; reloading once
// fetches the new index.html (and therefore the right chunk hashes) and the
// visitor never sees anything went wrong at all.
window.addEventListener("vite:preloadError", () => {
  window.location.reload();
});

// The site no longer uses a service worker (the push-notification one,
// /push-sw.js, only shows notifications and is kept — see lib/push.ts). The one people already had kept
// serving an old copy of the site after every deploy (see vite.config.ts);
// /sw.js is now a worker that removes itself. Any registration still left in
// this browser is dropped here too, with the caches it filled — the page
// itself always comes from the server from now on.
if ("serviceWorker" in navigator) {
  void navigator.serviceWorker
    .getRegistrations()
    .then((registrations) =>
      Promise.all(
        registrations
          .filter((r) => !(r.active ?? r.installing ?? r.waiting)?.scriptURL.endsWith("/push-sw.js"))
          .map((r) => r.unregister()),
      ),
    )
    .catch(() => undefined);
  if ("caches" in window) {
    void caches
      .keys()
      .then((names) => Promise.all(names.map((name) => caches.delete(name))))
      .catch(() => undefined);
  }
}

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root element");
applyAppearancePreferences();

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        // Respect a saved choice; new visitors start with Anivora's midnight theme.
        forcedTheme={FEATURES.lightTheme ? undefined : "dark"}
        enableSystem={FEATURES.lightTheme}
        disableTransitionOnChange
      >
        <I18nProvider>
          <AuthProvider>
            {/* Syncing the account's theme fetched the profile on every
                page; with one theme there is nothing to sync. */}
            {FEATURES.lightTheme && <ThemeSync />}
            <RouterProvider router={router} />
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
